import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus, Loader2, ArrowRight } from 'lucide-react';
import PropertyCard from './PropertyCard';

const STEP = 6;
const idOf = (p) => p._id || p.id;
// Merge without duplicates (StrictMode double effects / concurrent batches can return the same items).
const mergeUnique = (a, b) => {
  const seen = new Set(a.map(idOf));
  return [...a, ...b.filter((p) => !seen.has(idOf(p)) && seen.add(idOf(p)))];
};

// Phone-only "+ Voir plus": reveals the properties that don't fit in the carousel, 6 at a time,
// then (optionally) keeps going with listings fetched from the API.
export const MobileSeeMore = ({ pool, excludeIds, fetchMore, testId }) => {
  const { i18n } = useTranslation();
  const [shown, setShown] = useState(0);
  const [fetched, setFetched] = useState([]);
  const [busy, setBusy] = useState(false);
  const state = useRef({ skip: 0, exhausted: !fetchMore });

  const loadBatch = useCallback(async () => {
    const s = state.current;
    if (s.exhausted) return [];
    const seen = new Set([...excludeIds, ...pool.map(idOf)]);
    const batch = await fetchMore(s.skip).catch(() => []);
    s.skip += batch.length;
    if (batch.length === 0) s.exhausted = true;
    return batch.filter((p) => !seen.has(idOf(p)));
  }, [excludeIds, pool, fetchMore]);

  // Prefetch one batch so the button only appears when there really is more.
  useEffect(() => {
    if (!fetchMore || state.current.prefetched) return;
    state.current.prefetched = true;
    loadBatch().then((b) => setFetched((f) => mergeUnique(f, b)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const all = [...pool, ...fetched];
  const canMore = all.length > shown;

  const onClick = async () => {
    const target = shown + STEP;
    setBusy(true);
    let extra = [];
    let tries = 0;
    while (all.length + extra.length < target + 1 && !state.current.exhausted && tries < 3) {
      extra = [...extra, ...(await loadBatch())];
      tries += 1;
    }
    if (extra.length) setFetched((f) => mergeUnique(f, extra));
    setShown(target);
    setBusy(false);
  };

  const isEn = (i18n.language || 'fr').startsWith('en');
  const label = isEn ? 'More Properties' : 'Voir plus';
  return (
    <div className="mt-6" data-testid={`${testId}-see-more`}>
      {shown > 0 && (
        <div className="grid grid-cols-1 gap-6 mb-6" data-testid={`${testId}-see-more-list`}>
          {all.slice(0, shown).map((p, i) => (
            <div key={idOf(p)} className="animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: `${(i % STEP) * 60}ms`, animationFillMode: 'backwards' }}>
              <PropertyCard property={p} />
            </div>
          ))}
        </div>
      )}
      {canMore && (
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          className="w-full h-12 rounded-full border-2 border-stone-900 text-stone-900 font-semibold inline-flex items-center justify-center gap-2 hover:bg-stone-900 hover:text-white active:scale-[0.98] transition-[background-color,color,transform] duration-200 disabled:opacity-60"
          data-testid={`${testId}-see-more-btn`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {label}
        </button>
      )}
      {shown > 0 && (
        <Link
          to="/properties"
          className="mt-4 flex items-center justify-center gap-1.5 text-sm font-semibold text-brand-600 hover:underline"
          data-testid={`${testId}-see-all-link`}
        >
          {isEn ? 'View all listings' : 'Voir toutes les annonces'}
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
};

export default MobileSeeMore;
